import { useState } from 'react'
import { X, ChevronDown } from 'lucide-react'
import type { Medication } from '../../store/medicationsSlice'

interface EditMedicationModalProps {
  medication: Medication
  onSave: (data: Partial<Medication>) => Promise<void>
  onClose: () => void
}

const CATEGORIES = ['Antibiotic', 'Anti-inflammatory', 'Analgesic', 'Sedative', 'Vaccine', 'Parasiticide', 'Hormone', 'Other']
const FORMS = ['Tablet', 'Capsule', 'Liquid', 'Injectable', 'Topical', 'Chewable']
const FREQUENCIES = ['SID', 'BID', 'TID', 'QID', 'q8h', 'q12h', 'PRN']
const DEA_SCHEDULES = ['II', 'III', 'IV', 'V']

export function EditMedicationModal({ medication, onSave, onClose }: EditMedicationModalProps) {
  const [name, setName] = useState(medication.name)
  const [category, setCategory] = useState(medication.category)
  const [form, setForm] = useState(medication.form)
  const [concentration, setConcentration] = useState(medication.concentration)
  const [defaultDoseRate, setDefaultDoseRate] = useState(medication.default_dose_rate?.toString() || '')
  const [commonFrequency, setCommonFrequency] = useState(medication.common_frequency)
  const [stockQuantity, setStockQuantity] = useState(medication.stock_quantity.toString())
  const [reorderThreshold, setReorderThreshold] = useState(medication.reorder_threshold.toString())
  const [controlledSubstance, setControlledSubstance] = useState(medication.controlled_substance)
  const [deaSchedule, setDeaSchedule] = useState(medication.dea_schedule || '')
  const [knownInteractions, setKnownInteractions] = useState(medication.known_interactions)
  const [contraindicatedSpecies, setContraindicatedSpecies] = useState(medication.contraindicated_species)
  const [notes, setNotes] = useState(medication.notes)
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false)
  const [showFormDropdown, setShowFormDropdown] = useState(false)
  const [showFrequencyDropdown, setShowFrequencyDropdown] = useState(false)
  const [showDeaDropdown, setShowDeaDropdown] = useState(false)
  const [saving, setSaving] = useState(false)

  const closeAllDropdowns = () => {
    setShowCategoryDropdown(false)
    setShowFormDropdown(false)
    setShowFrequencyDropdown(false)
    setShowDeaDropdown(false)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim() || !category || !form || !concentration.trim()) return
    setSaving(true)
    await onSave({
      name: name.trim(),
      category,
      form,
      concentration: concentration.trim(),
      default_dose_rate: defaultDoseRate ? parseFloat(defaultDoseRate) : null,
      common_frequency: commonFrequency,
      stock_quantity: parseInt(stockQuantity) || 0,
      reorder_threshold: parseInt(reorderThreshold) || 0,
      controlled_substance: controlledSubstance,
      dea_schedule: controlledSubstance ? deaSchedule || null : null,
      known_interactions: knownInteractions,
      contraindicated_species: contraindicatedSpecies,
      notes,
    })
    setSaving(false)
  }

  const labelClass = 'text-[11px] text-text-muted block mb-0.5'
  const inputClass = 'w-full h-7 px-2 rounded-[4px] border border-border bg-base text-[13px] text-text-primary focus:outline-none focus:ring-2 focus:ring-accent/40'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" data-testid="edit-medication-modal">
      <div
        className="bg-surface border border-border rounded-[8px] w-full max-w-lg max-sm:max-w-[calc(100%-24px)] max-h-[85vh] overflow-y-auto"
        style={{ boxShadow: 'var(--shadow-elevation-2)' }}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-border-light">
          <h2 className="text-[14px] font-medium text-text-primary">Edit Medication</h2>
          <button
            data-testid="edit-medication-close"
            onClick={onClose}
            className="w-7 h-7 inline-flex items-center justify-center rounded-[4px] hover:bg-hover cursor-pointer"
          >
            <X size={14} className="text-text-muted" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-4 py-3 flex flex-col gap-3">
          <div>
            <label className={labelClass}>Medication Name *</label>
            <input
              data-testid="edit-medication-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={inputClass}
              required
            />
          </div>

          <div className="flex gap-3">
            <div className="flex-1">
              <label className={labelClass}>Category *</label>
              <div className="relative">
                <button
                  type="button"
                  data-testid="edit-medication-category"
                  onClick={() => {
                    closeAllDropdowns()
                    setShowCategoryDropdown(!showCategoryDropdown)
                  }}
                  className={`${inputClass} cursor-pointer flex items-center justify-between`}
                >
                  {category || 'Select category...'}
                  <ChevronDown size={12} className="text-text-muted" />
                </button>
                {showCategoryDropdown && (
                  <div
                    data-testid="edit-medication-category-dropdown"
                    className="absolute z-10 top-full left-0 w-full mt-1 bg-surface border border-border rounded-[4px] max-h-48 overflow-y-auto"
                    style={{ boxShadow: 'var(--shadow-elevation-1)' }}
                  >
                    {CATEGORIES.map((c) => (
                      <button
                        key={c}
                        type="button"
                        data-testid={`edit-category-option-${c}`}
                        onClick={() => {
                          setCategory(c)
                          setShowCategoryDropdown(false)
                        }}
                        className="w-full text-left px-2 py-1.5 text-[13px] text-text-primary hover:bg-hover cursor-pointer"
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex-1">
              <label className={labelClass}>Form *</label>
              <div className="relative">
                <button
                  type="button"
                  data-testid="edit-medication-form"
                  onClick={() => {
                    closeAllDropdowns()
                    setShowFormDropdown(!showFormDropdown)
                  }}
                  className={`${inputClass} cursor-pointer flex items-center justify-between`}
                >
                  {form || 'Select form...'}
                  <ChevronDown size={12} className="text-text-muted" />
                </button>
                {showFormDropdown && (
                  <div
                    data-testid="edit-medication-form-dropdown"
                    className="absolute z-10 top-full left-0 w-full mt-1 bg-surface border border-border rounded-[4px] max-h-40 overflow-y-auto"
                    style={{ boxShadow: 'var(--shadow-elevation-1)' }}
                  >
                    {FORMS.map((f) => (
                      <button
                        key={f}
                        type="button"
                        data-testid={`edit-form-option-${f}`}
                        onClick={() => {
                          setForm(f)
                          setShowFormDropdown(false)
                        }}
                        className="w-full text-left px-2 py-1.5 text-[13px] text-text-primary hover:bg-hover cursor-pointer"
                      >
                        {f}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div>
            <label className={labelClass}>Concentration *</label>
            <input
              data-testid="edit-medication-concentration"
              value={concentration}
              onChange={(e) => setConcentration(e.target.value)}
              className={inputClass}
              required
            />
          </div>

          <div className="flex gap-3">
            <div className="flex-1">
              <label className={labelClass}>Default Dose Rate (mg/kg)</label>
              <input
                data-testid="edit-medication-dose-rate"
                type="number"
                step="any"
                value={defaultDoseRate}
                onChange={(e) => setDefaultDoseRate(e.target.value)}
                className={inputClass}
              />
            </div>
            <div className="flex-1">
              <label className={labelClass}>Common Frequency</label>
              <div className="relative">
                <button
                  type="button"
                  data-testid="edit-medication-frequency"
                  onClick={() => {
                    closeAllDropdowns()
                    setShowFrequencyDropdown(!showFrequencyDropdown)
                  }}
                  className={`${inputClass} cursor-pointer flex items-center justify-between`}
                >
                  {commonFrequency || 'Select frequency...'}
                  <ChevronDown size={12} className="text-text-muted" />
                </button>
                {showFrequencyDropdown && (
                  <div
                    data-testid="edit-medication-frequency-dropdown"
                    className="absolute z-10 top-full left-0 w-full mt-1 bg-surface border border-border rounded-[4px] max-h-40 overflow-y-auto"
                    style={{ boxShadow: 'var(--shadow-elevation-1)' }}
                  >
                    {FREQUENCIES.map((f) => (
                      <button
                        key={f}
                        type="button"
                        data-testid={`edit-frequency-option-${f}`}
                        onClick={() => {
                          setCommonFrequency(f)
                          setShowFrequencyDropdown(false)
                        }}
                        className="w-full text-left px-2 py-1.5 text-[13px] text-text-primary hover:bg-hover cursor-pointer"
                      >
                        {f}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <div className="flex-1">
              <label className={labelClass}>Stock Quantity</label>
              <input
                data-testid="edit-medication-stock-quantity"
                type="number"
                value={stockQuantity}
                onChange={(e) => setStockQuantity(e.target.value)}
                className={inputClass}
              />
            </div>
            <div className="flex-1">
              <label className={labelClass}>Reorder Threshold</label>
              <input
                data-testid="edit-medication-reorder-threshold"
                type="number"
                value={reorderThreshold}
                onChange={(e) => setReorderThreshold(e.target.value)}
                className={inputClass}
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              data-testid="edit-medication-controlled"
              type="checkbox"
              checked={controlledSubstance}
              onChange={(e) => setControlledSubstance(e.target.checked)}
              className="w-4 h-4 rounded accent-accent"
            />
            <label className="text-[13px] text-text-primary">Controlled Substance</label>
          </div>

          {controlledSubstance && (
            <div>
              <label className={labelClass}>DEA Schedule</label>
              <div className="relative">
                <button
                  type="button"
                  data-testid="edit-medication-dea-schedule"
                  onClick={() => {
                    closeAllDropdowns()
                    setShowDeaDropdown(!showDeaDropdown)
                  }}
                  className={`${inputClass} cursor-pointer flex items-center justify-between`}
                >
                  {deaSchedule || 'Select schedule...'}
                  <ChevronDown size={12} className="text-text-muted" />
                </button>
                {showDeaDropdown && (
                  <div
                    data-testid="edit-medication-dea-dropdown"
                    className="absolute z-10 top-full left-0 w-full mt-1 bg-surface border border-border rounded-[4px] max-h-40 overflow-y-auto"
                    style={{ boxShadow: 'var(--shadow-elevation-1)' }}
                  >
                    {DEA_SCHEDULES.map((s) => (
                      <button
                        key={s}
                        type="button"
                        data-testid={`edit-dea-option-${s}`}
                        onClick={() => {
                          setDeaSchedule(s)
                          setShowDeaDropdown(false)
                        }}
                        className="w-full text-left px-2 py-1.5 text-[13px] text-text-primary hover:bg-hover cursor-pointer"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <div>
            <label className={labelClass}>Known Interactions (comma separated)</label>
            <textarea
              data-testid="edit-medication-interactions"
              value={knownInteractions}
              onChange={(e) => setKnownInteractions(e.target.value)}
              className={`${inputClass} h-auto min-h-[60px] py-1.5`}
            />
          </div>

          <div>
            <label className={labelClass}>Contraindicated Species (comma separated)</label>
            <textarea
              data-testid="edit-medication-contraindicated-species"
              value={contraindicatedSpecies}
              onChange={(e) => setContraindicatedSpecies(e.target.value)}
              className={`${inputClass} h-auto min-h-[60px] py-1.5`}
            />
          </div>

          <div>
            <label className={labelClass}>Notes</label>
            <textarea
              data-testid="edit-medication-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className={`${inputClass} h-auto min-h-[60px] py-1.5`}
            />
          </div>

          <div className="flex justify-end gap-2 mt-1">
            <button
              type="button"
              data-testid="edit-medication-cancel"
              onClick={onClose}
              className="inline-flex items-center h-7 px-3 rounded-[4px] border border-border text-[13px] text-text-primary hover:bg-hover transition-colors duration-100 cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="edit-medication-save"
              disabled={saving || !name.trim() || !concentration.trim()}
              className="inline-flex items-center h-7 px-3 rounded-[4px] bg-accent text-white text-[13px] font-normal hover:bg-accent-dark transition-colors duration-100 cursor-pointer disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
